// frontend/src/components/chat/ChatSecurityModal.js
// Modal de verificacion de seguridad para el chat

import React, { useState, useEffect } from 'react';
import './ChatSecurityModal.css';

const ChatSecurityModal = ({ isOpen, onAccept, onCancel, userId }) => {
    const [deviceInfo, setDeviceInfo] = useState({
        browser: '',
        os: '',
        ip: '',
        location: '',
        dateTime: ''
    });
    const [trustLevel, setTrustLevel] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            detectDeviceInfo();
        }
    }, [isOpen]);

    const detectDeviceInfo = async () => {
        try {
            setLoading(true);

            // Detectar navegador
            const userAgent = navigator.userAgent;
            let browser = 'Desconocido';
            if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Google Chrome';
            else if (userAgent.includes('Firefox')) browser = 'Mozilla Firefox';
            else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
            else if (userAgent.includes('Edg')) browser = 'Microsoft Edge';
            else if (userAgent.includes('Opera') || userAgent.includes('OPR')) browser = 'Opera';

            // Detectar sistema operativo
            let os = 'Desconocido';
            if (userAgent.includes('Windows NT 10')) os = 'Windows 10/11';
            else if (userAgent.includes('Windows')) os = 'Windows';
            else if (userAgent.includes('Mac OS X')) os = 'macOS';
            else if (userAgent.includes('Linux')) os = 'Linux';
            else if (userAgent.includes('Android')) os = 'Android';
            else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) os = 'iOS';

            // Obtener IP y ubicacion desde API gratuita
            let ip = 'No disponible';
            let location = 'No disponible';

            try {
                const ipResponse = await fetch('https://ipapi.co/json/', {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' }
                });

                if (ipResponse.ok) {
                    const ipData = await ipResponse.json();
                    ip = ipData.ip || 'No disponible';
                    location = `${ipData.city || ''}, ${ipData.region || ''}, ${ipData.country_name || ''}`.replace(/^, |, $/g, '') || 'No disponible';
                }
            } catch (ipError) {
                console.warn('No se pudo obtener IP/ubicacion:', ipError);
                try {
                    const altResponse = await fetch('https://ip-api.com/json/?fields=status,country,regionName,city,query');
                    if (altResponse.ok) {
                        const altData = await altResponse.json();
                        if (altData.status === 'success') {
                            ip = altData.query || 'No disponible';
                            location = `${altData.city || ''}, ${altData.regionName || ''}, ${altData.country || ''}`.replace(/^, |, $/g, '') || 'No disponible';
                        }
                    }
                } catch (altError) {
                    console.warn('API alternativa tambien fallo:', altError);
                }
            }

            // Fecha y hora actual
            const now = new Date();
            const dateTime = now.toLocaleString('es-EC', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            setDeviceInfo({
                browser,
                os,
                ip,
                location,
                dateTime,
                userAgent: userAgent.substring(0, 100)
            });

            setLoading(false);
        } catch (err) {
            console.error('Error detectando dispositivo:', err);
            setError('Error al detectar informacion del dispositivo');
            setLoading(false);
        }
    };

    // IMPORTANTE: Debe ser identico a generateDeviceFingerprint en ChatWindow.js
    const generateDeviceFingerprint = () => {
        const userAgent = navigator.userAgent;
        const data = `${userAgent}-${navigator.language}-${window.screen.width}x${window.screen.height}`;
        let hash = 0;
        for (let i = 0; i < data.length; i++) {
            const char = data.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16);
    };

    const handleAccept = async () => {
        if (!trustLevel) {
            alert('Por favor selecciona una opcion de confianza');
            return;
        }
        if (!acceptedTerms) {
            alert('Debes aceptar los terminos de privacidad para continuar');
            return;
        }
        if (trustLevel === 'untrusted') {
            onCancel();
            return;
        }

        const fingerprint = generateDeviceFingerprint();
        const trustData = {
            fingerprint,
            deviceInfo,
            trustLevel,
            rememberDevice: trustLevel === 'personal'
        };

        onAccept(trustData);
    };

    if (!isOpen) return null;

    return (
        <div className="urbanq-security-overlay">
            <div className="urbanq-security-modal">
                {/* Header */}
                <div className="urbanq-security-header">
                    <div className="urbanq-security-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                            <path d="M9 12l2 2 4-4"/>
                        </svg>
                    </div>
                    <h2>Verificacion de Seguridad</h2>
                    <p>Antes de acceder a tus mensajes cifrados</p>
                </div>

                {/* Device Info */}
                <div className="urbanq-security-device-info">
                    <h3>Informacion del dispositivo detectado</h3>

                    {loading ? (
                        <div className="urbanq-security-loading">
                            <div className="urbanq-security-spinner"></div>
                            <span>Detectando dispositivo...</span>
                        </div>
                    ) : error ? (
                        <div className="urbanq-security-error">{error}</div>
                    ) : (
                        <div className="urbanq-security-info-grid">
                            <div className="urbanq-security-info-item">
                                <span className="urbanq-security-info-icon">📍</span>
                                <div>
                                    <label>Ubicacion</label>
                                    <span>{deviceInfo.location}</span>
                                </div>
                            </div>
                            <div className="urbanq-security-info-item">
                                <span className="urbanq-security-info-icon">💻</span>
                                <div>
                                    <label>Dispositivo</label>
                                    <span>{deviceInfo.os} - {deviceInfo.browser}</span>
                                </div>
                            </div>
                            <div className="urbanq-security-info-item">
                                <span className="urbanq-security-info-icon">🌐</span>
                                <div>
                                    <label>Direccion IP</label>
                                    <span>{deviceInfo.ip}</span>
                                </div>
                            </div>
                            <div className="urbanq-security-info-item">
                                <span className="urbanq-security-info-icon">📅</span>
                                <div>
                                    <label>Fecha y hora</label>
                                    <span>{deviceInfo.dateTime}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Trust Options */}
                <div className="urbanq-security-trust-section">
                    <h3>Confias en este dispositivo?</h3>

                    <div className="urbanq-security-options">
                        <label className={`urbanq-security-option ${trustLevel === 'personal' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="trust"
                                value="personal"
                                checked={trustLevel === 'personal'}
                                onChange={(e) => setTrustLevel(e.target.value)}
                            />
                            <div className="urbanq-security-option-content">
                                <span className="urbanq-security-option-icon">🏠</span>
                                <div>
                                    <strong>Si, es mi dispositivo personal</strong>
                                    <small>No volveremos a preguntar en este dispositivo (30 dias)</small>
                                </div>
                            </div>
                        </label>

                        <label className={`urbanq-security-option ${trustLevel === 'shared' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="trust"
                                value="shared"
                                checked={trustLevel === 'shared'}
                                onChange={(e) => setTrustLevel(e.target.value)}
                            />
                            <div className="urbanq-security-option-content">
                                <span className="urbanq-security-option-icon">🏢</span>
                                <div>
                                    <strong>Si, pero es un dispositivo compartido</strong>
                                    <small>Se verificara cada vez que inicies sesion</small>
                                </div>
                            </div>
                        </label>

                        <label className={`urbanq-security-option danger ${trustLevel === 'untrusted' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="trust"
                                value="untrusted"
                                checked={trustLevel === 'untrusted'}
                                onChange={(e) => setTrustLevel(e.target.value)}
                            />
                            <div className="urbanq-security-option-content">
                                <span className="urbanq-security-option-icon">⚠️</span>
                                <div>
                                    <strong>No reconozco este dispositivo</strong>
                                    <small>Se cancelara el acceso al chat</small>
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Terms */}
                <div className="urbanq-security-terms">
                    <label className="urbanq-security-checkbox">
                        <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                        />
                        <span className="urbanq-security-checkmark"></span>
                        <span>
                            He leido y acepto los{' '}
                            <button
                                type="button"
                                className="urbanq-security-link"
                                onClick={() => setShowTerms(true)}
                            >
                                terminos de privacidad y proteccion de datos
                            </button>
                        </span>
                    </label>
                </div>

                {/* Actions */}
                <div className="urbanq-security-actions">
                    <button
                        className="urbanq-security-btn secondary"
                        onClick={onCancel}
                    >
                        Cancelar
                    </button>
                    <button
                        className="urbanq-security-btn primary"
                        onClick={handleAccept}
                        disabled={!trustLevel || !acceptedTerms || loading}
                    >
                        <span>Continuar al Chat</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                    </button>
                </div>

                {/* Terms Modal */}
                {showTerms && (
                    <div className="urbanq-security-terms-modal">
                        <div className="urbanq-security-terms-content">
                            <button
                                className="urbanq-security-terms-close"
                                onClick={() => setShowTerms(false)}
                            >
                                ✕
                            </button>
                            <h3>🔒 Politica de Privacidad - Chat UrbanQ</h3>

                            <div className="urbanq-security-terms-body">
                                <h4>1. Cifrado de Mensajes</h4>
                                <p>Tus mensajes estan protegidos con cifrado de extremo a extremo.
                                Solo tu y el destinatario pueden leer el contenido de las conversaciones.</p>

                                <h4>2. Almacenamiento Seguro</h4>
                                <p>UrbanQ almacena tus mensajes de forma segura en servidores protegidos.
                                No tenemos acceso al contenido de tus conversaciones privadas.</p>

                                <h4>3. Archivos Compartidos</h4>
                                <p>Los archivos que compartas (imagenes, documentos) se almacenan con un
                                limite maximo de 5MB por archivo. Estos archivos estan protegidos y solo
                                son accesibles por los participantes de la conversacion.</p>

                                <h4>4. Dispositivos de Confianza</h4>
                                <p>Cuando marcas un dispositivo como "personal", guardamos una identificacion
                                unica (fingerprint) para reconocerlo. Esta informacion expira automaticamente
                                despues de 30 dias o cuando cierres sesion.</p>

                                <h4>5. Derecho al Olvido</h4>
                                <p>Puedes eliminar tu historial de chat en cualquier momento desde la
                                configuracion. Tambien puedes revocar el acceso de dispositivos confiables.</p>

                                <h4>6. Datos Recopilados</h4>
                                <p>Para tu seguridad, recopilamos informacion basica del dispositivo:</p>
                                <ul>
                                    <li>Tipo de navegador y sistema operativo</li>
                                    <li>Direccion IP aproximada</li>
                                    <li>Ubicacion general (ciudad/pais)</li>
                                    <li>Fecha y hora de acceso</li>
                                </ul>
                                <p>Esta informacion se usa exclusivamente para proteger tu cuenta.</p>

                                <h4>7. Notificaciones de Seguridad</h4>
                                <p>Te notificaremos si detectamos accesos sospechosos o desde
                                ubicaciones inusuales.</p>

                                <h4>8. Contacto</h4>
                                <p>Para dudas sobre privacidad: soporte@nextunicodecorp.com</p>
                            </div>

                            <button
                                className="urbanq-security-btn primary"
                                onClick={() => setShowTerms(false)}
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatSecurityModal;
